import { useState, useEffect } from 'react'
import { supabase } from '../lib/supabase'

const TABS = [
  { id: 'datos', label: 'Datos', icon: '📋' },
  { id: 'accesos', label: 'Accesos', icon: '↑' },
  { id: 'paquetes', label: 'Paquetes', icon: '📦' },
  { id: 'reclamos', label: 'Reclamos', icon: '⚠️' },
  { id: 'gremios', label: 'Gremios', icon: '🔧' },
  { id: 'salas', label: 'Salas', icon: '📅', onlyRawson: true },
  { id: 'novedades', label: 'Novedades', icon: '📝' },
]

function formatFecha(dateStr) {
  const d = new Date(dateStr)
  return d.toLocaleDateString('es-AR', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })
}

function formatFechaCorta(dateStr) {
  const d = new Date(dateStr)
  return d.toLocaleDateString('es-AR', { day: 'numeric', month: 'short' })
}

export default function FichaOficina({ oficina, onClose }) {
  const [tab, setTab] = useState('datos')
  const [stats, setStats] = useState({ accesos: 0, paquetes: 0, reclamos: 0, gremios: 0, salas: 0, novedades: 0 })
  const [data, setData] = useState([])
  const [contactos, setContactos] = useState([])
  const [loading, setLoading] = useState(false)

  const esRawson = oficina.edificios?.nombre?.toLowerCase().includes('rawson')
  const tabsDisponibles = TABS.filter(t => !t.onlyRawson || esRawson)

  // Scrollear al tope y bloquear scroll del body cuando el drawer está abierto
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' })
    document.body.style.overflow = 'hidden'
    return () => { document.body.style.overflow = '' }
  }, [])

  // Cargar stats (últimos 30 días)
  useEffect(() => {
    const hace30 = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString()
    const cargarStats = async () => {
      const [acc, paq, rec, gre, sal, nov] = await Promise.all([
        supabase.from('accesos').select('id', { count: 'exact', head: true }).eq('oficina_id', oficina.id).gte('created_at', hace30),
        supabase.from('paquetes').select('id', { count: 'exact', head: true }).eq('oficina_id', oficina.id).gte('created_at', hace30),
        supabase.from('reclamos').select('id', { count: 'exact', head: true }).eq('oficina_id', oficina.id).gte('created_at', hace30),
        supabase.from('gremios').select('id', { count: 'exact', head: true }).eq('oficina_id', oficina.id).gte('created_at', hace30),
        esRawson ? supabase.from('reservas_salas').select('id', { count: 'exact', head: true }).eq('oficina_id', oficina.id).gte('created_at', hace30) : Promise.resolve({ count: 0 }),
        supabase.from('novedades').select('id', { count: 'exact', head: true }).eq('oficina_id', oficina.id).gte('created_at', hace30),
      ])
      setStats({
        accesos: acc.count || 0,
        paquetes: paq.count || 0,
        reclamos: rec.count || 0,
        gremios: gre.count || 0,
        salas: sal.count || 0,
        novedades: nov.count || 0,
      })
    }
    cargarStats()
  }, [oficina.id, esRawson])

  // Cargar data de la tab activa
  useEffect(() => {
    if (tab === 'datos') {
      supabase.from('contactos_oficina').select('*').eq('oficina_id', oficina.id).eq('activo', true).order('apellido')
        .then(({ data }) => setContactos(data || []))
      return
    }

    setLoading(true)
    const cargarData = async () => {
      let query, select
      switch (tab) {
        case 'accesos':
          query = supabase.from('accesos').select('*').eq('oficina_id', oficina.id).order('created_at', { ascending: false }).limit(50)
          break
        case 'paquetes':
          query = supabase.from('paquetes').select('*').eq('oficina_id', oficina.id).order('created_at', { ascending: false }).limit(50)
          break
        case 'reclamos':
          query = supabase.from('reclamos').select('*').eq('oficina_id', oficina.id).order('created_at', { ascending: false }).limit(50)
          break
        case 'gremios':
          query = supabase.from('gremios').select('*').eq('oficina_id', oficina.id).order('created_at', { ascending: false }).limit(50)
          break
        case 'salas':
          query = supabase.from('reservas_salas').select('*').eq('oficina_id', oficina.id).order('created_at', { ascending: false }).limit(50)
          break
        case 'novedades':
          query = supabase.from('novedades').select('*').eq('oficina_id', oficina.id).order('created_at', { ascending: false }).limit(50)
          break
        default:
          setData([])
          setLoading(false)
          return
      }
      const { data: result } = await query
      setData(result || [])
      setLoading(false)
    }
    cargarData()
  }, [tab, oficina.id])

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 2000, background: 'rgba(0,0,0,0.7)',
      display: 'flex', justifyContent: 'flex-end',
    }} onClick={onClose}>
      <div style={{
        width: '90%', maxWidth: 800, height: '100vh', background: 'var(--bg)',
        boxShadow: '-8px 0 32px rgba(0,0,0,0.5)', display: 'flex', flexDirection: 'column',
        animation: 'slideInRight 0.25s ease-out',
      }} onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div style={{ padding: '24px 28px', borderBottom: '1px solid var(--border)', background: 'var(--bg2)' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <span style={{ fontSize: 24, fontWeight: 700, color: 'var(--gold)', fontFamily: 'DM Mono, monospace' }}>
                  {oficina.numero}
                </span>
                <span style={{ fontSize: 13, color: 'var(--text3)' }}>{oficina.edificios?.nombre}</span>
              </div>
              <div style={{ fontSize: 16, fontWeight: 600, color: 'var(--text1)', marginTop: 4 }}>
                {oficina.titular}
              </div>
            </div>
            <button onClick={onClose} style={{
              background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text3)',
              fontSize: 28, padding: 8, lineHeight: 1,
            }}>✕</button>
          </div>

          {/* Mini stats */}
          <div style={{
            display: 'flex', gap: 16, flexWrap: 'wrap', padding: '12px 0', fontSize: 12, color: 'var(--text3)',
          }}>
            <span>👥 {stats.accesos} visitas</span>
            <span>📦 {stats.paquetes} paquetes</span>
            <span>⚠️ {stats.reclamos} reclamos</span>
            <span>🔧 {stats.gremios} gremios</span>
            {esRawson && <span>📅 {stats.salas} reservas</span>}
            <span>📝 {stats.novedades} novedades</span>
            <span style={{ color: 'var(--text3)', fontSize: 11, fontStyle: 'italic' }}>· Últimos 30 días</span>
          </div>
        </div>

        {/* Tabs */}
        <div style={{
          display: 'flex', gap: 4, padding: '12px 28px', background: 'var(--bg2)',
          borderBottom: '1px solid var(--border)', overflowX: 'auto',
        }}>
          {tabsDisponibles.map(t => (
            <button key={t.id}
              onClick={() => setTab(t.id)}
              style={{
                padding: '8px 14px', borderRadius: 6, border: 'none', cursor: 'pointer',
                background: tab === t.id ? 'var(--gold-dim)' : 'transparent',
                color: tab === t.id ? 'var(--gold)' : 'var(--text3)',
                fontSize: 13, fontWeight: tab === t.id ? 600 : 400,
                whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: 6,
              }}>
              <span>{t.icon}</span>
              {t.label}
            </button>
          ))}
        </div>

        {/* Content */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '20px 28px' }}>
          {tab === 'datos' && (
            <div>
              <div style={{ marginBottom: 20 }}>
                <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 8, fontFamily: 'DM Mono, monospace', letterSpacing: 1 }}>INFORMACIÓN DE CONTACTO</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {oficina.email_contacto && (
                    <div style={{ fontSize: 13 }}>
                      <span style={{ color: 'var(--text3)' }}>Email 1:</span>{' '}
                      <span style={{ fontFamily: 'DM Mono, monospace', color: 'var(--text1)' }}>{oficina.email_contacto}</span>
                    </div>
                  )}
                  {oficina.email_contacto_2 && (
                    <div style={{ fontSize: 13 }}>
                      <span style={{ color: 'var(--text3)' }}>Email 2:</span>{' '}
                      <span style={{ fontFamily: 'DM Mono, monospace', color: 'var(--text1)' }}>{oficina.email_contacto_2}</span>
                    </div>
                  )}
                  {oficina.telefono && (
                    <div style={{ fontSize: 13 }}>
                      <span style={{ color: 'var(--text3)' }}>Teléfono:</span>{' '}
                      <span style={{ fontFamily: 'DM Mono, monospace', color: 'var(--text1)' }}>{oficina.telefono}</span>
                    </div>
                  )}
                </div>
              </div>

              <div>
                <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 8, fontFamily: 'DM Mono, monospace', letterSpacing: 1 }}>
                  CONTACTOS ({contactos.length})
                </div>
                {contactos.length === 0 ? (
                  <div style={{ fontSize: 13, color: 'var(--text3)', padding: '20px 0' }}>Sin contactos registrados</div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {contactos.map(c => (
                      <div key={c.id} style={{
                        padding: '10px 14px', background: 'var(--card)', borderRadius: 8,
                        border: '1px solid var(--border)',
                      }}>
                        <div style={{ fontSize: 14, fontWeight: 500, color: 'var(--text1)' }}>
                          {c.nombre} {c.apellido}
                        </div>
                        {c.email && (
                          <div style={{ fontSize: 12, color: 'var(--text3)', fontFamily: 'DM Mono, monospace', marginTop: 2 }}>
                            {c.email}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {tab === 'accesos' && (
            <div>
              {loading ? (
                <div style={{ textAlign: 'center', padding: 40, color: 'var(--text3)' }}>Cargando...</div>
              ) : data.length === 0 ? (
                <div style={{ textAlign: 'center', padding: 40, color: 'var(--text3)' }}>Sin accesos registrados</div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {data.map(item => (
                    <div key={item.id} style={{
                      padding: '12px 16px', background: 'var(--card)', borderRadius: 8,
                      border: '1px solid var(--border)',
                    }}>
                      <div style={{ fontSize: 14, fontWeight: 500, color: 'var(--text1)' }}>
                        {item.nombre} {item.apellido}
                      </div>
                      <div style={{ fontSize: 12, color: 'var(--text3)', marginTop: 4 }}>
                        DNI {item.dni} · {formatFecha(item.created_at)}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {tab === 'paquetes' && (
            <div>
              {loading ? (
                <div style={{ textAlign: 'center', padding: 40, color: 'var(--text3)' }}>Cargando...</div>
              ) : data.length === 0 ? (
                <div style={{ textAlign: 'center', padding: 40, color: 'var(--text3)' }}>Sin paquetes registrados</div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {data.map(item => (
                    <div key={item.id} style={{
                      padding: '12px 16px', background: 'var(--card)', borderRadius: 8,
                      border: '1px solid var(--border)',
                    }}>
                      <div style={{ fontSize: 14, fontWeight: 500, color: 'var(--text1)' }}>
                        Para: {item.destinatario}
                      </div>
                      {item.numero_envio && (
                        <div style={{ fontSize: 12, color: 'var(--text3)', marginTop: 2 }}>
                          N° {item.numero_envio}
                        </div>
                      )}
                      <div style={{ fontSize: 12, color: 'var(--text3)', marginTop: 4 }}>
                        {formatFecha(item.created_at)} · {item.retirado ? '✓ Retirado' : 'Pendiente'}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {tab === 'reclamos' && (
            <div>
              {loading ? (
                <div style={{ textAlign: 'center', padding: 40, color: 'var(--text3)' }}>Cargando...</div>
              ) : data.length === 0 ? (
                <div style={{ textAlign: 'center', padding: 40, color: 'var(--text3)' }}>Sin reclamos registrados</div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {data.map(item => (
                    <div key={item.id} style={{
                      padding: '12px 16px', background: 'var(--card)', borderRadius: 8,
                      border: `1px solid ${item.prioridad === 'alta' ? '#e05555' : item.prioridad === 'media' ? 'var(--gold)' : 'var(--border)'}`,
                      borderLeft: `3px solid ${item.prioridad === 'alta' ? '#e05555' : item.prioridad === 'media' ? 'var(--gold)' : '#6db8a0'}`,
                    }}>
                      <div style={{ fontSize: 14, fontWeight: 500, color: 'var(--text1)' }}>
                        {item.titulo}
                      </div>
                      {item.categoria && (
                        <div style={{ fontSize: 11, color: 'var(--gold)', marginTop: 2, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                          {item.categoria}
                        </div>
                      )}
                      <div style={{ fontSize: 12, color: 'var(--text3)', marginTop: 4 }}>
                        {formatFechaCorta(item.created_at)} · {item.estado}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {tab === 'gremios' && (
            <div>
              {loading ? (
                <div style={{ textAlign: 'center', padding: 40, color: 'var(--text3)' }}>Cargando...</div>
              ) : data.length === 0 ? (
                <div style={{ textAlign: 'center', padding: 40, color: 'var(--text3)' }}>Sin visitas técnicas registradas</div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {data.map(item => (
                    <div key={item.id} style={{
                      padding: '12px 16px', background: 'var(--card)', borderRadius: 8,
                      border: '1px solid var(--border)',
                    }}>
                      <div style={{ fontSize: 14, fontWeight: 500, color: 'var(--text1)' }}>
                        {item.tipo_servicio}
                      </div>
                      {item.empresa && (
                        <div style={{ fontSize: 12, color: 'var(--text3)', marginTop: 2 }}>
                          {item.empresa}
                        </div>
                      )}
                      {item.tecnico_nombre && (
                        <div style={{ fontSize: 12, color: 'var(--text3)', marginTop: 2 }}>
                          Técnico: {item.tecnico_nombre}
                        </div>
                      )}
                      <div style={{ fontSize: 12, color: 'var(--text3)', marginTop: 4 }}>
                        {formatFecha(item.created_at)}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {tab === 'salas' && (
            <div>
              {loading ? (
                <div style={{ textAlign: 'center', padding: 40, color: 'var(--text3)' }}>Cargando...</div>
              ) : data.length === 0 ? (
                <div style={{ textAlign: 'center', padding: 40, color: 'var(--text3)' }}>Sin reservas de salas</div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {data.map(item => (
                    <div key={item.id} style={{
                      padding: '12px 16px', background: 'var(--card)', borderRadius: 8,
                      border: '1px solid var(--border)',
                    }}>
                      <div style={{ fontSize: 14, fontWeight: 500, color: 'var(--gold)' }}>
                        {item.sala === 'grande' ? 'Sala Grande' : item.sala === 'chica' ? 'Sala Chica' : 'Sala de Conferencias'}
                      </div>
                      <div style={{ fontSize: 12, color: 'var(--text3)', marginTop: 2 }}>
                        {item.reservante}
                      </div>
                      <div style={{ fontSize: 12, color: 'var(--text3)', marginTop: 4 }}>
                        {formatFechaCorta(item.fecha)} · {item.hora_inicio}:00 - {item.hora_fin}:00 hs
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {tab === 'novedades' && (
            <div>
              {loading ? (
                <div style={{ textAlign: 'center', padding: 40, color: 'var(--text3)' }}>Cargando...</div>
              ) : data.length === 0 ? (
                <div style={{ textAlign: 'center', padding: 40, color: 'var(--text3)' }}>Sin novedades relacionadas</div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {data.map(item => (
                    <div key={item.id} style={{
                      padding: '12px 16px', background: 'var(--card)', borderRadius: 8,
                      border: '1px solid var(--border)',
                    }}>
                      <div style={{ fontSize: 14, fontWeight: 500, color: 'var(--text1)' }}>
                        {item.tipo === 'llave' ? '🔑' : item.tipo === 'recordatorio' ? '⏰' : item.tipo === 'pendiente' ? '⚡' : '📝'} {item.titulo}
                      </div>
                      {item.descripcion && (
                        <div style={{ fontSize: 12, color: 'var(--text2)', marginTop: 4, whiteSpace: 'pre-wrap' }}>
                          {item.descripcion}
                        </div>
                      )}
                      <div style={{ fontSize: 12, color: 'var(--text3)', marginTop: 4 }}>
                        {formatFecha(item.created_at)} · {item.estado}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        <style>{`
          @keyframes slideInRight {
            from { transform: translateX(100%); }
            to { transform: translateX(0); }
          }
        `}</style>
      </div>
    </div>
  )
}
